import React, { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

const CATEGORIES = [
  { slug: 'kabiner', name: 'Kabiner', desc: 'Blæsekabiner til tør- og glasblæsning' },
  { slug: 'vandpoleringsmaskiner', name: 'Vandpoleringsmaskiner', desc: 'KWB vandpolere i fuldsvejst rustfrit stål' },
  { slug: 'mobile-blaesere', name: 'Mobile Blæsere', desc: 'Ibix mobile blæsesystemer' },
  { slug: 'kompressorer', name: 'Kompressorer', desc: 'Stempel- og skruekompressorer fra Reno' },
  { slug: 'toerisblaesning', name: 'Tørisblæsning', desc: 'Kärcher Ice Blasters med L2P teknologi' },
  { slug: 'forbrugsmaterialer', name: 'Forbrugsmaterialer & Blæsemedier', desc: 'SuperShine, Glasball, Rustoff & kemi' },
  { slug: 'reservedele-tilbehoer', name: 'Reservedele & Tilbehør', desc: 'Handsker, viskersæt, ruder & riste' },
]

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false)
  const dropdownRef = useRef(null)
  const location = useLocation()

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false)
    setDropdownOpen(false)
    setMobileProductsOpen(false)
  }, [location.pathname, location.search])

  // Click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const isProductsActive = location.pathname.startsWith('/produkt')

  return (
    <header className="sticky top-0 z-50 w-full bg-slate-900 border-b border-slate-800 text-slate-100 shadow-md">
      {/* Main navigation container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 sm:h-24">
          {/* Brand Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 py-2 group focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 rounded"
            aria-label="Krüger Wet Blaster ApS - Forside"
          >
            <img
              src="https://assets.auxodev.dk/assets/21ecc947-a8ab-45f2-a8fa-709ae6f8815d/kruger-logo-negativ.svg"
              alt="Krüger Wet Blaster"
              className="h-12 sm:h-14 lg:h-16 w-auto object-contain transition-transform duration-200 group-hover:scale-[1.02]"
            />
          </Link>

          {/* Desktop Menu - ONLY rendered on screens >= 1200px */}
          <nav className="hidden min-[1200px]:flex items-center space-x-1" aria-label="Hovednavigation">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-white font-semibold'
                    : 'text-slate-300 hover:text-white'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Products dropdown */}
            <div
              className="relative"
              ref={dropdownRef}
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1.5 cursor-pointer select-none ${
                  isProductsActive
                    ? 'text-white font-semibold'
                    : 'text-slate-300 hover:text-white'
                }`}
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                <span>Produkter</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    dropdownOpen ? 'rotate-180 text-sky-400' : 'text-slate-400'
                  }`}
                  aria-hidden="true"
                />
              </button>

              {/* Dropdown panel */}
              {dropdownOpen && (
                <div className="absolute top-full left-0 w-72 pt-2 shadow-2xl z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                  <div className="rounded-lg bg-slate-900 border border-slate-700/80 p-1.5 shadow-xl ring-1 ring-black/20">
                    {CATEGORIES.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/produkter?kategori=${cat.slug}`}
                        className="block px-3 py-2 rounded-md hover:bg-slate-800/90 text-sm font-medium text-slate-100 hover:text-white transition-colors"
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-white font-semibold'
                    : 'text-slate-300 hover:text-white'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink
              to="/galleri"
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-white font-semibold'
                    : 'text-slate-300 hover:text-white'
                }`
              }
            >
              Galleri
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-white font-semibold'
                    : 'text-slate-300 hover:text-white'
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          {/* Right Action Button on Desktop */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            <Link
              to="/bestil"
              className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 active:bg-sky-700 text-white text-sm font-semibold px-5 py-2.5 rounded-md shadow-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            >
              <i className="fa-solid fa-file-invoice text-xs" aria-hidden="true" />
              <span>Bestil produkt</span>
            </Link>
          </div>

          {/* Burger menu button - ONLY visible on screens < 1200px */}
          <div className="flex items-center gap-3 min-[1200px]:hidden">
            <a
              href="tel:+4521438149"
              className="p-2 text-sky-400 hover:text-sky-300 transition-colors"
              aria-label="Ring til os: 21 43 81 49"
            >
              <i className="fa-solid fa-phone text-lg" aria-hidden="true" />
            </a>

            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="inline-flex items-center justify-center p-2 text-slate-300 hover:text-white bg-transparent hover:bg-transparent active:bg-transparent focus:outline-none transition-colors"
              aria-controls="mobile-menu"
              aria-expanded={mobileOpen}
              aria-label="Åbn navigationsmenu"
            >
              <i
                className={`fa-solid ${mobileOpen ? 'fa-xmark' : 'fa-bars'} text-xl`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile / Tablet Drawer (< 1200px) */}
      {mobileOpen && (
        <div
          id="mobile-menu"
          className="min-[1200px]:hidden bg-slate-900 border-b border-slate-800 px-4 pt-2 pb-6 space-y-2 shadow-2xl animate-in fade-in duration-200"
        >
          <div className="space-y-1">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `block px-4 py-2.5 rounded-md text-base font-medium ${
                  isActive ? 'text-white font-semibold' : 'text-slate-300 hover:text-white'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Mobile Products Accordion */}
            <div>
              <button
                type="button"
                onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                className={`w-full flex items-center justify-between px-4 py-2.5 rounded-md text-base font-medium text-left transition-colors cursor-pointer ${
                  isProductsActive ? 'text-white font-semibold' : 'text-slate-300 hover:text-white'
                }`}
                aria-expanded={mobileProductsOpen}
              >
                <span>Produkter</span>
                <i
                  className={`fa-solid fa-chevron-down text-sm transition-transform duration-200 ${
                    mobileProductsOpen ? 'rotate-180 text-sky-400' : 'text-slate-400'
                  }`}
                  aria-hidden="true"
                />
              </button>

              {mobileProductsOpen && (
                <div className="pl-4 pr-2 py-2 space-y-1 bg-slate-950/40 rounded-md mt-1 border-l-2 border-sky-500">
                  {CATEGORIES.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/produkter?kategori=${cat.slug}`}
                      className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-800/50 rounded"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `block px-4 py-2.5 rounded-md text-base font-medium ${
                  isActive ? 'text-white font-semibold' : 'text-slate-300 hover:text-white'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink
              to="/galleri"
              className={({ isActive }) =>
                `block px-4 py-2.5 rounded-md text-base font-medium ${
                  isActive ? 'text-white font-semibold' : 'text-slate-300 hover:text-white'
                }`
              }
            >
              Galleri
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `block px-4 py-2.5 rounded-md text-base font-medium ${
                  isActive ? 'text-white font-semibold' : 'text-slate-300 hover:text-white'
                }`
              }
            >
              Kontakt
            </NavLink>
          </div>

          <div className="pt-4 border-t border-slate-800 space-y-3">
            <Link
              to="/bestil"
              className="w-full flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 text-white font-semibold py-3 px-4 rounded-md shadow"
            >
              <i className="fa-solid fa-file-invoice text-sm" aria-hidden="true" />
              <span>Bestil produkt</span>
            </Link>

            <div className="px-2 pt-2 text-xs text-white space-y-1">
              <div>
                Tlf:{' '}
                <a
                  href="tel:+4521438149"
                  className="text-white hover:underline whitespace-nowrap font-medium"
                >
                  (+45) 21 43 81 49
                </a>
              </div>
              <div>
                Mail:{' '}
                <a
                  href="mailto:jonkrygerpetersen@gmail.com"
                  className="text-white hover:underline whitespace-nowrap"
                >
                  jonkrygerpetersen@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
